import { useState } from 'react';
import { useCustomers } from '@/hooks/useCustomers';
import { theme } from '@/lib/theme';
import AdminSidebar from '@/pages/admin/components/AdminSidebar';
import AdminTopBar from '@/pages/admin/components/AdminTopBar';
import PageHeader from '@/components/feature/PageHeader';
import CustomersTable from './components/CustomersTable';
import CustomerDetailModal from './components/CustomerDetailModal';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(amount);
}

export default function CustomersPage() {
  const { customers, allCustomers, loading, error, search, setSearch, refetch } = useCustomers();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const totalCustomers = allCustomers.length;
  const totalRevenue = allCustomers.reduce((sum, c) => sum + c.total_spent, 0);
  const avgSpent = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
  const returning = allCustomers.filter((c) => c.total_appointments > 1).length;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: theme.colors.bgBase }}>
      <AdminSidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar />
        <main className="flex-1 px-6 py-8 space-y-6">
          <div className="flex items-center justify-between">
            <PageHeader
              title="Clientes"
              subtitle="Base de datos de clientes y su historial de citas"
            />
            <button
              onClick={refetch}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap hover:bg-white/8"
              style={{ color: theme.colors.textSecondary, border: `1px solid ${theme.colors.border}` }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line text-sm" />
              </div>
              Actualizar
            </button>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total clientes', value: totalCustomers.toString(), icon: 'ri-group-line' },
              { label: 'Clientes recurrentes', value: returning.toString(), icon: 'ri-repeat-line' },
              { label: 'Ingresos totales', value: formatCurrency(totalRevenue), icon: 'ri-money-dollar-circle-line' },
              { label: 'Gasto promedio', value: formatCurrency(avgSpent), icon: 'ri-bar-chart-line' },
            ].map((kpi) => (
              <div
                key={kpi.label}
                className="rounded-xl p-5 border"
                style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: theme.colors.primaryMuted }}
                  >
                    <i className={`${kpi.icon} text-sm`} style={{ color: theme.colors.primary }} />
                  </div>
                  <p className="text-white/40 text-xs">{kpi.label}</p>
                </div>
                <p className="text-white text-xl font-bold">{loading ? '—' : kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Search */}
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl border"
            style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
          >
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-search-line text-sm" style={{ color: theme.colors.textMuted }} />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email o teléfono..."
              className="flex-1 bg-transparent text-sm text-white placeholder-white/25 outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                style={{ color: theme.colors.textMuted }}
              >
                <i className="ri-close-line text-xs" />
              </button>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-3 rounded-xl px-4 py-3 border border-red-500/20 bg-red-500/8">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-error-warning-line text-sm text-red-400" />
              </div>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Table */}
          <CustomersTable
            customers={customers}
            loading={loading}
            onSelect={setSelectedCustomerId}
          />

          {/* Count */}
          {!loading && customers.length > 0 && (
            <p className="text-white/25 text-xs text-right">
              {customers.length} cliente{customers.length !== 1 ? 's' : ''} encontrado{customers.length !== 1 ? 's' : ''}
            </p>
          )}
        </main>
      </div>

      {/* Detail modal */}
      <CustomerDetailModal
        customerId={selectedCustomerId}
        onClose={() => setSelectedCustomerId(null)}
      />
    </div>
  );
}
